import { Component, h, Prop, EventEmitter, Event, State, Host, Watch } from '@stencil/core';

@Component({
  tag: 'presto-playlist-overlay',
  styleUrl: 'presto-playlist-overlay.scss',
  shadow: true,
})
export class PrestoPlaylistOverlay {
  /**	Caching timout action */
  private timeout: any;

  /** Title of the upcoming next playlist item */
  @Prop() nextItemTitle: string;

  /** String for the Button that says `Next Video` */
  @Prop() nextItemString: string;

  /** Transition duration for next video. */
  @Prop() transitionDuration: number = 5;

  /** Flag to handle if this item is the last item in the list. */
  @Prop() isLastItem: boolean = false;

  /** Rewatch item event */
  @Event() rewatch: EventEmitter<void>;

  /** Next item play event */
  @Event() next: EventEmitter<void>;

  /** Class value for the Next button to show the fill animation. */
  @State() animating: boolean = false;

  /** Visibility flag for the Overlay */
  @Prop() show: boolean = false;

  /**
   * Watch for show changes
   */
  @Watch('show')
  handleShow() {
    // we are not showing or this is the last item.
    if (!this.show || this.isLastItem) {
      this.animating = false;
      return;
    }

    // start animating after 200ms
    setTimeout(() => {
      this.animating = true;
    }, 200);

    this.timeout = setTimeout(() => {
      if (this.show) {
        this.handleNext();
      }
    }, (this.transitionDuration || 5) * 1000 + 200);
  }

  /**
   * Handle the rewatch click event.
   */
  handleRewatch() {
    this.rewatch.emit();
  }

  /**
   * Handle the next click event.
   */
  handleNext() {
    this.next.emit();
  }

  /**
   * Lifecycle: disconnected
   */
  disconnectedCallback() {
    clearTimeout(this.timeout);
  }

  @Watch('animating')
  handleAnimatingChange(val) {
    if (val) return;
    clearTimeout(this.timeout);
  }

  /**
   * Rendering the component
   */
  render() {
    if (!this.show) {
      return (
        <Host>
          <div style={{ display: 'none' }}></div>
        </Host>
      );
    }
    const upNext = window?.prestoPlayer?.i18n?.upNext || 'Up Next';
    const startOver = window?.prestoPlayer?.i18n?.startOver || 'Start Over';
    const rewatch = window?.prestoPlayer?.i18n?.rewatch || 'Rewatch';
    const next = window?.prestoPlayer?.i18n?.next || 'Next';
    return (
      <div
        class="playlist__overlay"
        onClick={e => {
          // This prevents the video from playing/pausing when the overlay is clicked.
          e.preventDefault();
          e.stopImmediatePropagation();
          this.animating = false;
        }}
      >
        <div class="playlist__overlay-wrap">
          {!this.isLastItem ? <p>{upNext}:</p> : <p>{startOver}:</p>}

          <h2
            style={{
              overflowX: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              lineHeight: '1.3',
            }}
          >
            {this.nextItemTitle}
          </h2>

          <div class="playlist__overlay-buttons">
            <button class="previous" onClick={() => this.handleRewatch()}>
              <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 0V9L8 4.5L15 0Z" fill="white" />
                <path d="M7 0V9L0 4.5L7 0Z" fill="white" />
              </svg>
              {rewatch}
            </button>
            <button
              class={{
                'next': true,
                'fill-button': this.animating,
              }}
              onClick={e => {
                e.preventDefault();
                e.stopImmediatePropagation();
                this.handleNext();
              }}
            >
              <svg width="10" height="12" viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 0V12L9.5 6L0 0Z" fill="black" />
              </svg>
              {!this.isLastItem ? next + ' ' + (this?.nextItemString || 'Video') : startOver}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
